
import React, { useState, useEffect } from 'react';
import { FileText, Loader2, Trash2, Save, X, Edit2, Search, Filter, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { fetchPublicDataPoints, updatePublicDataPoint, deletePublicDataPoint } from '../services/publicDataService';
import { fetchDataCategories } from '../services/categoryService';
import { fetchDataSources } from '../services/dataSourceService';
import { PublicDataPoint, DataCategory, DataSource } from '../types';

const PublicDataView: React.FC = () => {
  // Data State
  const [dataPoints, setDataPoints] = useState<PublicDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter Options State
  const [categories, setCategories] = useState<DataCategory[]>([]);
  const [sources, setSources] = useState<DataSource[]>([]);

  // Filter & Pagination State
  const [page, setPage] = useState(1);
  const [limit] = useState(15); // Items per page
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSource, setSelectedSource] = useState('');

  // Edit State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [editUnit, setEditUnit] = useState('');
  const [editSummary, setEditSummary] = useState('');

  // Initial Load (Options)
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [cats, srcs] = await Promise.all([fetchDataCategories(), fetchDataSources()]);
        setCategories(cats);
        setSources(srcs);
      } catch (e) {
        console.error("Failed to load filter options", e);
      }
    };
    loadOptions();
  }, []);

  // Fetch Data on Filter/Page Change
  useEffect(() => {
    loadData();
  }, [page, selectedCategory, selectedSource]); 
  // Note: searchTerm is handled via specific submit/blur or debounce to prevent rapid firing, 
  // but for simplicity we'll trigger on Enter key in the input or Refresh button.

  const loadData = async () => {
    setLoading(true);
    try {
      const { data, count } = await fetchPublicDataPoints({
        page,
        limit,
        search: searchTerm,
        categoryId: selectedCategory,
        sourceId: selectedSource
      });
      setDataPoints(data);
      setTotalCount(count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(1); // Reset to first page on new search
      loadData();
    }
  };

  const handleFilterChange = (type: 'category' | 'source', value: string) => {
    if (type === 'category') setSelectedCategory(value);
    if (type === 'source') setSelectedSource(value);
    setPage(1); // Reset to page 1 on filter change
  };

  const startEdit = (p: PublicDataPoint) => {
    setEditingId(p.id);
    setEditValue(p.value_numeric ?? 0); // Default to 0 if undefined
    setEditUnit(p.unit);
    setEditSummary(p.summary_text);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id: string) => {
    try {
        await updatePublicDataPoint(id, {
            value_numeric: editValue,
            unit: editUnit,
            summary_text: editSummary
        });
        
        // Optimistic update
        setDataPoints(prev => prev.map(p => p.id === id ? { ...p, value_numeric: editValue, unit: editUnit, summary_text: editSummary } : p));
        setEditingId(null);
    } catch (e) {
        alert("Failed to update record");
    }
  };

  const handleDelete = async (id: string) => {
      if (!window.confirm("Permanently delete this data point?")) return;
      try {
          await deletePublicDataPoint(id);
          setDataPoints(prev => prev.filter(p => p.id !== id));
          setTotalCount(prev => prev - 1);
      } catch (e) {
          alert("Failed to delete record");
      }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-end border-b border-slate-700 pb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <FileText className="text-indigo-500" />
            Public Data Ledger
          </h1>
          <p className="text-slate-400">
            Master records of aggregated data points from all sources.
          </p>
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
        {/* Filters Toolbar */}
        <div className="p-4 border-b border-slate-700 bg-slate-900/50 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400" size={16} />
                    <input 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleSearchSubmit}
                      placeholder="Keyword Search..." 
                      className="bg-slate-800 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none w-full md:w-64"
                    />
                </div>

                {/* Category Filter */}
                <div className="relative">
                    <select 
                      value={selectedCategory} 
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded-lg pl-3 pr-8 py-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    <Filter className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={12} />
                </div>

                {/* Source Filter */}
                <div className="relative">
                    <select 
                      value={selectedSource} 
                      onChange={(e) => handleFilterChange('source', e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded-lg pl-3 pr-8 py-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors max-w-[200px]"
                    >
                        <option value="">All Sources</option>
                        {sources.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <Filter className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={12} />
                </div>
            </div>

            <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 font-mono">
                    {totalCount} Records
                </span>
                <button 
                  onClick={loadData} 
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg border border-slate-600 transition-all active:scale-95"
                  title="Refresh Data"
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>
        </div>

        {loading ? (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
            </div>
        ) : (
            <>
            <div className="overflow-x-auto min-h-[400px]">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-900 text-slate-400 font-semibold uppercase text-xs">
                        <tr>
                            <th className="px-6 py-4">Date</th>
                            <th className="px-6 py-4">Category</th>
                            {/* Source Column Removed */}
                            <th className="px-6 py-4">Value</th>
                            <th className="px-6 py-4">Context / Summary</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                        {dataPoints.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                    No data found matching your filters.
                                </td>
                            </tr>
                        )}
                        {dataPoints.map(p => (
                            <tr key={p.id} className="hover:bg-slate-700/30 transition-colors group">
                                <td className="px-6 py-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                                    {p.date_reported}
                                </td>
                                <td className="px-6 py-4">
                                    <span className="inline-block px-2 py-1 rounded bg-indigo-900/30 text-indigo-300 text-xs border border-indigo-500/20">
                                        {p.category?.name || 'Uncategorized'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    {editingId === p.id ? (
                                        <div className="flex gap-2 items-center">
                                            <input type="number" value={editValue} onChange={e => setEditValue(parseFloat(e.target.value))} className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"/>
                                            <input value={editUnit} onChange={e => setEditUnit(e.target.value)} className="w-16 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"/>
                                        </div>
                                    ) : (
                                        <span className="font-mono text-emerald-400 font-bold text-base">
                                            {p.value_numeric?.toLocaleString() ?? (p.value_text || '-')} <span className="text-slate-500 text-xs font-normal ml-1">{p.unit}</span>
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-slate-300 max-w-md truncate" title={p.summary_text}>
                                    {editingId === p.id ? (
                                        <input value={editSummary} onChange={e => setEditSummary(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-white text-xs"/>
                                    ) : (
                                        p.summary_text
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    {editingId === p.id ? (
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => saveEdit(p.id)} className="p-1.5 bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded transition-colors"><Save size={16}/></button>
                                            <button onClick={cancelEdit} className="p-1.5 bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-white rounded transition-colors"><X size={16}/></button>
                                        </div>
                                    ) : (
                                        <div className="flex justify-end gap-3 opacity-60 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => startEdit(p)} className="text-slate-400 hover:text-indigo-400 transition-colors"><Edit2 size={16}/></button>
                                            <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={16}/></button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="bg-slate-900/50 px-6 py-4 border-t border-slate-700 flex justify-between items-center">
                <div className="text-xs text-slate-500">
                    Page {page} of {totalPages || 1}
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 border border-slate-600 text-slate-300 text-xs hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft size={14} /> Previous
                    </button>
                    <button 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 border border-slate-600 text-slate-300 text-xs hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            </div>
            </>
        )}
      </div>
    </div>
  );
};

export default PublicDataView;
